import { Product as CatalogProduct } from "gen/catalog/models_pb"
import { SaleForm_CustomField as CustomField } from "gen/company/models_pb"
import { LedgerMethods } from "gen/ledger/models_pb"
import { Product as PBProduct } from "gen/product/models_pb"
import { TintColor } from "gen/tintcolor/models_pb"
import {
  Sale_Source as SaleSource,
  Sale_Totals,
  Sale_Type as SaleTypePB,
  SaleProduct,
  Sale_Status as SaleStatus,
} from "gen/txn/models_pb"

import { Decimal } from "../../classes/decimal"
import { FractionalMoney, Money } from "../../classes/money"
import { UUID } from "../../classes/uuid"

export interface SaleTaxLine {
  key: string
  label: string
  tax: Money
}

export interface SaleLocation {
  id: UUID
  name: string
  prefix: string
}

export interface SaleLinkedSale {
  id: UUID
  identifier: string
  type: SaleType
}

export interface SaleStatusEvent {
  date?: Date
  byName: string
}

export interface SaleCustomer {
  id: UUID
  name: string
  identifier: string
}

export interface SaleBuyer {
  name: string
  phone: string
}

export const SaleTypes = {
  SALE: SaleTypePB.SALE,
  RETURN: SaleTypePB.RETURN,
}

export type SaleType = (typeof SaleTypes)[keyof typeof SaleTypes]

export const SaleStatusLabels = {
  [SaleStatus.UNDEFINED]: undefined,
  [SaleStatus.CREATED]: "Created",
  [SaleStatus.QUOTED]: "Quoted",
  [SaleStatus.WILL_CALLED]: "Will called",
  [SaleStatus.SOLD]: "Sold",
  [SaleStatus.PAID]: "Sold",
  [SaleStatus.VOIDED]: "Voided",
  [SaleStatus.SPECIAL_ORDER]: "Special order",
} as const satisfies Record<SaleStatus, string | undefined>

export type SaleStatusLabel = NonNullable<
  (typeof SaleStatusLabels)[keyof typeof SaleStatusLabels]
>

export interface BaseSale {
  id: UUID
  type: SaleType
  identifier: string
  customer?: SaleCustomer
  location: SaleLocation
  statusEvent: SaleStatusEvent
  notes: string
  returnedIn?: SaleLinkedSale[]
  returnedFrom?: SaleLinkedSale[]
  deliveryIds?: UUID[]
  poName?: string
  addressLine1?: string
  buyer?: SaleBuyer
  saleStatus: SaleStatus
  source: SaleSource
  saleProducts: SaleProduct[]
  productsById: Record<string, PBProduct>
  amountPaidByCustomer?: Money
  catalogProductsById: Record<string, CatalogProduct>
  tintColorsById: Record<string, TintColor>
  ledgerMethods?: LedgerMethods
  internalNotes: string
  aggregatedTotals?: Sale_Totals
  cardSurchargeRate?: Decimal
  baseTaxLines: SaleTaxLine[]
  surchargeTaxLines: SaleTaxLine[]
}

export interface SaleKit {
  id: UUID
  kitProductId: UUID
  isActive: boolean
  position: number
  quantity: Decimal
  // Per kit.
  price: FractionalMoney
  isPriceEdited: boolean
  cost: FractionalMoney
  standardCost: FractionalMoney
  expectedPrice: FractionalMoney
  notes: string
  internalNotes: string
  customFields: CustomField[]
}

export interface CustomerSale extends Omit<BaseSale, "internalNotes"> {
  customer: SaleCustomer
}
